import React, { FC, useCallback, useContext, useEffect } from 'react';

import { Grid, Table } from '@material-ui/core';

import { GlobalContext } from '../../../../../../store';
import { IAdminEventResultsStore } from '../../../../../../store/admin/eventResults/store';
import { IMetaFormat } from '../../../../../../store/admin/roundFormats/store';
import { ScoreType } from '../../../../../../store/admin/eventFormats/store';
import { IAdminRoundResultsStore } from '../../../../../../store/admin/roundResults/store';

import { asyncForEach } from '../../../../../../contrib/utils';
import {
  useAssignResultsDispatch,
  useAssignResultsState
} from '../../../Common/AssignResults/Context/AssignResults.context';
import { isHorse } from '../../../Common/ResultsTable/Utils/isHorse';
import { isStrokeplayOrHorse } from '../../../Common/ResultsTable/Utils/isStrokeplayOrHorse';
import { HorseRaceTableBody } from './Components/HorseRaceTableBody/HorseRaceTableBody';
import { SkinsTableBody } from './Components/SkinsTableBody/SkinsTableBody';
import { StillInRaceTable } from './Components/StillInRaceTable/StillInRaceTable';
import { StrokeTableBody } from './Components/StrokeTableBody/StrokeTableBody';
import TeamStrokeTableBody from './Components/TeamsStrokeTableBody/TeamsStrokeTableBody';
import { isCTP } from '../../../Common/ResultsTable/Utils/isCTP';
import { CTPTableBody } from './Components/CTPTableBody/Components/CTPTableBody';
import { ResultsTableHead } from './ResultsTableHead';
import { ScrambleTableBody } from './Components/ScrambleTableBody/ScrambleTableBody';
import { useRouteInfoState } from '../../../../../../components/RouteInfo/Context/RouteInfo.context';

const ResultsTable: FC = () => {
  const { clubId, eventId, roundId } = useRouteInfoState();

  const { horseRaceEliminatedResults, results, selectedCombination } = useAssignResultsState();

  const { dispatch } = useAssignResultsDispatch();

  const {
    actions: {
      adminEventFormatAwards: { get: getEventFormatAwards },
      adminEventResults: { get: getEventResults },
      adminRoundFormatAwards: { get: getRoundFormatAwards },
      adminRoundResults: { get: getRoundResults },
      adminHoleResults: { get: getHoleResults },
      adminRoundAwards: { get: getAdminRoundAwards },
      roundScores: { get: getRoundScores }
    },
    adminEventFormatAwards,
    adminEventFormats,
    adminEventResults,
    adminRoundFormatAwards,
    adminRoundResults
  } = useContext(GlobalContext);

  useEffect(() => {
    if (selectedCombination) {
      if (isStrokeplayOrHorse(selectedCombination)) {
        if (roundId) {
          getRoundResults(roundId);
        } else {
          getEventResults(eventId);
        }
      } else {
        getHoleResults(roundId);
      }
    }
  }, [roundId, eventId, selectedCombination]);

  useEffect(() => {
    if (roundId && selectedCombination) {
      getRoundFormatAwards(roundId, selectedCombination.id)
        .then()
        .catch();
    }
  }, [roundId, selectedCombination]);

  useEffect(() => {
    if (!roundId && clubId && eventId && adminEventFormats && adminEventFormats[Number(eventId)]) {
      asyncForEach(adminEventFormats[Number(eventId)], (eventFormat: IMetaFormat) =>
        getEventFormatAwards(clubId, eventId, eventFormat.id)
      )
        .then()
        .catch();
    }
  }, [clubId, eventId, adminEventFormats]);

  const getSetResultsValue = useCallback(
    (resultsStore: IAdminRoundResultsStore | IAdminEventResultsStore, eventOrRoundId: number) =>
      selectedCombination &&
      isStrokeplayOrHorse(selectedCombination) &&
      resultsStore &&
      eventOrRoundId &&
      resultsStore[eventOrRoundId]
        ? selectedCombination.flight
          ? resultsStore[eventOrRoundId][selectedCombination.flight.id]
            ? resultsStore[eventOrRoundId][selectedCombination.flight.id]
            : []
          : Object.values(resultsStore[eventOrRoundId]).flat()
        : [],
    [selectedCombination]
  );

  useEffect(() => {
    const value = getSetResultsValue(adminRoundResults, Number(roundId));

    dispatch({
      type: 'setResults',
      value
    });
  }, [adminRoundResults, selectedCombination, roundId]);

  useEffect(() => {
    if (!roundId) {
      const value = getSetResultsValue(adminEventResults, Number(eventId));

      dispatch({
        type: 'setResults',
        value
      });
    }
  }, [adminEventResults, selectedCombination, eventId]);

  useEffect(() => {
    if (
      adminRoundFormatAwards &&
      roundId &&
      adminRoundFormatAwards[Number(roundId)] &&
      selectedCombination &&
      selectedCombination.format.id &&
      adminRoundFormatAwards[Number(roundId)][selectedCombination.format.id]
    ) {
      dispatch({
        type: 'setUserAwards',
        value: adminRoundFormatAwards[Number(roundId)][selectedCombination.format.id]
      });
    }
  }, [adminRoundFormatAwards, selectedCombination, roundId]);

  useEffect(() => {
    if (
      adminEventFormatAwards &&
      adminEventFormatAwards[Number(eventId)] &&
      selectedCombination &&
      selectedCombination.format.id &&
      adminEventFormatAwards[Number(eventId)][selectedCombination.format.id] &&
      isStrokeplayOrHorse(selectedCombination)
    ) {
      dispatch({
        type: 'setUserAwards',
        value: adminEventFormatAwards[Number(eventId)][selectedCombination.format.id]
      });
    }
  }, [adminEventFormatAwards, selectedCombination, eventId]);

  useEffect(() => {
    if (selectedCombination && isStrokeplayOrHorse(selectedCombination)) {
      dispatch({ type: 'sortResults' });
    }
  }, [results, selectedCombination]);

  useEffect(() => {
    if (selectedCombination && isStrokeplayOrHorse(selectedCombination)) {
      getAdminRoundAwards(clubId, eventId, roundId);
      getRoundScores(clubId, eventId, roundId);
    }
  }, [selectedCombination]);

  useEffect(() => {
    if (!adminRoundResults[-1] && selectedCombination && isStrokeplayOrHorse(selectedCombination)) {
      getRoundResults(-1, eventId);
    }
  }, [adminRoundResults]);

  const TableBodies = {
    ctp: <CTPTableBody roundId={Number(roundId)} />,
    gbn: <SkinsTableBody />,
    horse: <HorseRaceTableBody />,
    moneyBall: <p>To be implemented</p>,
    moneyball: <p>To be implemented</p>,
    scramble: <ScrambleTableBody />,
    skins: <SkinsTableBody />,
    stroke: <StrokeTableBody />,
    teamstroke: <TeamStrokeTableBody />
  };

  const isGBNorCTP =
    selectedCombination &&
    (selectedCombination.format.format.prefix.includes('gbn') ||
      selectedCombination.format.format.prefix.includes('ctp'));

  return (
    <div className="results-table">
      {selectedCombination ? (
        <div className={ScoreType[selectedCombination.format.scoreType]}>
          <Grid className="grid combination-information" container={true} spacing={8}>
            <Grid className="grid-item" item={true} xs={12}>
              <p className="formatName">{selectedCombination.format.format.name}</p>
              {!isGBNorCTP && (
                <p className="scoreTypeName">{ScoreType[selectedCombination.scoreType]}</p>
              )}
            </Grid>
          </Grid>

          {isHorse(selectedCombination) &&
            horseRaceEliminatedResults &&
            horseRaceEliminatedResults.false && <StillInRaceTable />}

          <Table className={`results-table-table ${isCTP(selectedCombination) ? 'ctp' : ''}`}>
            <colgroup>
              {[0, 1, 2, 3, 4, 6, 7].map(index => (
                <col key={index} />
              ))}
            </colgroup>

            <ResultsTableHead />

            {TableBodies[selectedCombination.format.format.prefix]}
          </Table>
        </div>
      ) : (
        <p className="noResults">
          Please, select a combination from the list below the filters on the left area
        </p>
      )}
    </div>
  );
};

export default ResultsTable;
